import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Camera, Grid3X3, ChevronDown, Check,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { HoloStickerCard } from '../components/HoloStickerCard';
import { flagFor, specialIconFor, isCocaCola, pageMatchesQuery, codeMatchesQuery } from '../lib/flags';
import { useI18n } from '../lib/i18n';

type PageGroup = {
  pageNumber: number;
  title: string;
  stickers: any[];
  have: number;
  total: number;
  duplicates: number;
  pct: number;
};

export function MyAlbum() {
  const { token } = useAuth();
  const qc = useQueryClient();
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const album = 'mundial-2026';

  const incMut = useMutation({
    mutationFn: ({ stickerId, delta }: { stickerId: string; delta: number }) =>
      api.incrementSticker(stickerId, delta, token!),
    onMutate: async ({ stickerId, delta }) => {
      await qc.cancelQueries({ queryKey: ['collection'] });
      const prev = qc.getQueryData<any>(['collection']);
      if (prev?.stickers) {
        const next = {
          ...prev,
          stickers: prev.stickers.map((s: any) =>
            s.id === stickerId ? { ...s, quantity: Math.max(0, (s.quantity ?? 0) + delta) } : s,
          ),
        };
        qc.setQueryData(['collection'], next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['collection'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['collection'] });
      qc.invalidateQueries({ queryKey: ['duplicates'] });
      qc.invalidateQueries({ queryKey: ['missing'] });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });

  const { data: detail } = useQuery({
    queryKey: ['album', album],
    queryFn: () => api.albumDetail(album, token!),
  });
  const { data: col } = useQuery({
    queryKey: ['collection'],
    queryFn: () => api.collection(token!),
  });

  const all = col?.stickers ?? [];
  const searching = query.trim().length > 0;
  const q = query.trim().toLowerCase();

  // Agrupar TODA la colección por página, con stats
  const groups = useMemo<PageGroup[]>(() => {
    if (!detail?.pages) return [];
    const stickersByPage = new Map<number, any[]>();
    for (const s of all) {
      if (!stickersByPage.has(s.page_number)) stickersByPage.set(s.page_number, []);
      stickersByPage.get(s.page_number)!.push(s);
    }
    return detail.pages
      .map((p: any) => {
        const items = (stickersByPage.get(p.page_number) ?? []).sort(
          (a: any, b: any) => (a.position ?? 0) - (b.position ?? 0),
        );
        const have = items.filter((s) => s.quantity >= 1).length;
        const dupes = items.reduce(
          (sum, s) => sum + Math.max(0, s.quantity - 1),
          0,
        );
        const total = items.length;
        return {
          pageNumber: p.page_number,
          title: p.title ?? `Página ${p.page_number}`,
          stickers: items,
          have,
          total,
          duplicates: dupes,
          pct: total ? Math.round((have / total) * 100) : 0,
        };
      })
      .sort((a: PageGroup, b: PageGroup) => a.pageNumber - b.pageNumber);
  }, [detail, all]);

  // Filtrar por búsqueda — siempre devuelve ambos tipos de resultado:
  //   1) País(es) cuyo nombre matchea → mostrar país completo con TODOS sus stickers
  //   2) Otros países con jugadores/código/número que matchean → mostrar solo esos stickers
  const { countryMatches, playerMatches } = useMemo(() => {
    if (!searching) return { countryMatches: groups, playerMatches: [] as PageGroup[] };
    const cm = groups.filter((g) => pageMatchesQuery(g.title, q));
    const cmIds = new Set(cm.map((g) => g.pageNumber));
    const pm = groups
      .filter((g) => !cmIds.has(g.pageNumber))
      .map((g) => ({
        ...g,
        stickers: g.stickers.filter(
          (s: any) =>
            String(s.number) === q ||
            codeMatchesQuery(s.code, q) ||
            (s.player_name?.toLowerCase().includes(q) ?? false),
        ),
      }))
      .filter((g) => g.stickers.length > 0);
    return { countryMatches: cm, playerMatches: pm };
  }, [groups, q, searching]);

  const filteredGroups = useMemo(
    () => [...countryMatches, ...playerMatches],
    [countryMatches, playerMatches],
  );

  // Auto-expand SOLO en la transición de empezar/parar de buscar.
  // (No reaccionar a `filteredGroups` recalculado por mutaciones — colapsaba la página tras cada tap)
  const prevSearching = useRef(false);
  useEffect(() => {
    if (searching && !prevSearching.current) {
      setExpanded(new Set(filteredGroups.map((g) => g.pageNumber)));
    } else if (!searching && prevSearching.current) {
      setExpanded(new Set());
    }
    prevSearching.current = searching;
  }, [searching, filteredGroups]);

  function toggle(pn: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(pn) ? next.delete(pn) : next.add(pn);
      return next;
    });
  }

  function stateFor(qty: number): 'missing' | 'have' | 'duplicate' {
    return qty === 0 ? 'missing' : qty >= 2 ? 'duplicate' : 'have';
  }

  const overallPct = col?.completionPct ?? 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <header>
        <p className="text-2xs uppercase tracking-widest text-haze">{t('album.eyebrow')}</p>
        <h1 className="font-display font-extrabold text-3xl mt-0.5">
          {t('album.title')} <span className="text-gradient">2026</span>
        </h1>
        <p className="mt-1 text-sm text-haze">
          {col?.have ?? 0} {t('common.from')} {col?.total ?? 980} {t('album.subtitle')} · <span className="text-lime font-semibold">{overallPct}%</span> {t('album.complete')}
        </p>
      </header>

      {/* CTAs principales */}
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/app/upload"
          className="relative overflow-hidden rounded-2xl glass-strong p-4 flex items-center gap-3 hover:bg-graphite/40 transition group"
        >
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-flame/15 text-flame border border-flame/35 group-hover:bg-flame/25 transition">
            <Camera size={20} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-base leading-tight">{t('dash.uploadPhoto')}</div>
            <div className="text-2xs text-haze">{t('dash.uploadPhotoDesc')}</div>
          </div>
          <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-flame/20 blur-2xl group-hover:bg-flame/35 transition" />
        </Link>
        <Link
          to="/app/manual"
          className="relative overflow-hidden rounded-2xl glass-strong p-4 flex items-center gap-3 hover:bg-graphite/40 transition group"
        >
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-lime/15 text-lime border border-lime/35 group-hover:bg-lime/25 transition">
            <Grid3X3 size={20} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-base leading-tight">{t('dash.manualEntry')}</div>
            <div className="text-2xs text-haze">{t('dash.manualEntryDesc')}</div>
          </div>
          <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-lime/15 blur-2xl group-hover:bg-lime/30 transition" />
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-haze" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('common.searchPlaceholder')}
          className="input pl-9"
        />
      </div>

      {/* Lista de países desplegables */}
      <div className="space-y-2">
        {(() => {
          const renderRow = (g: PageGroup) => {
            const isOpen = expanded.has(g.pageNumber);
            const flag = flagFor(g.title);
            const isComplete = g.pct === 100;
            const isUntouched = g.have === 0;
            return (
              <PageRow
                key={g.pageNumber}
                group={g}
                isOpen={isOpen}
                flag={flag}
                isComplete={isComplete}
                isUntouched={isUntouched}
                onToggle={() => toggle(g.pageNumber)}
                stateFor={stateFor}
                onStickerClick={(s: any) =>
                  incMut.mutate({ stickerId: s.id, delta: s.quantity === 0 ? 1 : 0 })
                }
                onStickerInc={(s: any) =>
                  incMut.mutate({ stickerId: s.id, delta: 1 })
                }
                onStickerDec={(s: any) =>
                  s.quantity > 0
                    ? incMut.mutate({ stickerId: s.id, delta: -1 })
                    : null
                }
              />
            );
          };

          if (!searching) return filteredGroups.map(renderRow);

          return (
            <>
              {countryMatches.length > 0 && (
                <>
                  <h3 className="text-2xs uppercase tracking-widest text-haze font-bold ml-1 mt-1">
                    {t('album.searchCountries')} · {countryMatches.length}
                  </h3>
                  {countryMatches.map(renderRow)}
                </>
              )}
              {playerMatches.length > 0 && (
                <>
                  <h3 className="text-2xs uppercase tracking-widest text-haze font-bold ml-1 mt-3">
                    {t('album.searchPlayers')} · {playerMatches.reduce((s, g) => s + g.stickers.length, 0)}
                  </h3>
                  {playerMatches.map(renderRow)}
                </>
              )}
              {countryMatches.length === 0 && playerMatches.length === 0 && (
                <div className="glass rounded-2xl p-8 text-center text-haze text-sm">
                  {t('album.noResults')} "{query}".
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PageRow — fila desplegable por país
// ─────────────────────────────────────────────────────────────────────
function PageRow({
  group, isOpen, flag, isComplete, isUntouched, onToggle, stateFor,
  onStickerClick, onStickerInc, onStickerDec,
}: {
  group: PageGroup;
  isOpen: boolean;
  flag: string | null;
  isComplete: boolean;
  isUntouched: boolean;
  onToggle: () => void;
  stateFor: (q: number) => 'missing' | 'have' | 'duplicate';
  onStickerClick: (s: any) => void;
  onStickerInc: (s: any) => void;
  onStickerDec: (s: any) => void;
}) {
  const { t } = useI18n();
  const accent = isComplete ? 'lime' : isUntouched ? 'flame' : 'cyan';
  const accentClasses = {
    lime: {
      border: 'border-lime/35',
      glow: 'shadow-[0_0_24px_-10px_rgba(198,255,94,0.55)]',
      barFill: 'bg-lime',
      text: 'text-lime',
      iconBg: 'bg-lime/15 border-lime/30',
    },
    flame: {
      border: 'border-flame/30',
      glow: '',
      barFill: 'bg-flame/70',
      text: 'text-[#FF6B7A]',
      iconBg: 'bg-flame/10 border-flame/25',
    },
    cyan: {
      border: 'border-line/60',
      glow: 'shadow-[0_0_18px_-12px_rgba(94,224,255,0.4)]',
      barFill: 'bg-gradient-to-r from-lime to-cyan',
      text: 'text-cyan',
      iconBg: 'bg-cyan/10 border-cyan/25',
    },
  }[accent];

  return (
    <div className={`glass-strong rounded-2xl border ${accentClasses.border} ${accentClasses.glow} overflow-hidden`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition"
        aria-expanded={isOpen}
      >
        {/* icon: flag / special / coca-cola */}
        {isCocaCola(group.title) ? (
          <div className="shrink-0 grid h-12 w-12 rounded-xl place-items-center bg-flame border-2 border-flame font-display font-extrabold text-2xl text-white shadow-glow-flame italic">
            C
          </div>
        ) : (
          <div className={`shrink-0 grid h-12 w-12 rounded-xl place-items-center text-2xl ${accentClasses.iconBg} border`}>
            {flag ?? specialIconFor(group.title) ?? '✦'}
          </div>
        )}

        {/* info + progress */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-display font-extrabold text-base truncate">
              {group.title}
            </h3>
            <span className={`text-2xs font-bold tabular-nums shrink-0 ${accentClasses.text}`}>
              {group.have}/{group.total}
              {isComplete && <Check size={11} className="inline ml-1" />}
            </span>
          </div>
          {/* progress bar */}
          <div className="mt-1.5 h-1 rounded-full bg-white/8 overflow-hidden">
            <motion.div
              initial={false}
              animate={{ width: `${group.pct}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className={`h-full ${accentClasses.barFill}`}
            />
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-2xs text-haze tabular-nums">{group.pct}%</span>
            {group.duplicates > 0 && (
              <span className="text-2xs text-cyan font-semibold">
                · {group.duplicates} {group.duplicates === 1 ? t('album.dupes') : t('album.dupesPlural')}
              </span>
            )}
            {isUntouched && (
              <span className="text-2xs text-haze italic">· {t('album.untouched')}</span>
            )}
          </div>
        </div>

        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="shrink-0 text-haze"
        >
          <ChevronDown size={20} />
        </motion.div>
      </button>

      {/* Contenido desplegable: grid de stickers */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-4 pt-1 border-t border-line/40">
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                {group.stickers.map((s: any) => (
                  <HoloStickerCard
                    key={s.id}
                    number={s.number}
                    code={s.code}
                    rarity={s.rarity}
                    state={stateFor(s.quantity)}
                    duplicates={Math.max(0, s.quantity - 1)}
                    playerName={s.player_name}
                    team={s.team}
                    imageUrl={s.image_url}
                    size="md"
                    onClick={() => onStickerClick(s)}
                    onIncrement={() => onStickerInc(s)}
                    onDecrement={s.quantity > 0 ? () => onStickerDec(s) : undefined}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
